// Logix AI ERP — "Living Dot-Field" assistant mark.
// A canvas halftone particle field (NOT a solid orb, NOT a Gemini sparkle):
// a grid of dots that continuously RE-FORM into different shapes — round,
// square, oval, and a flowing halftone sheet — while a brand-colour gradient
// (indigo -> violet -> blue -> sky) drifts across the dots.
//
// The DOTS THEMSELVES adopt the macro shape: circle dots when the field is
// round/halftone, square dots when it is a square, elongated dots when oval —
// and they morph smoothly between those as the shape cycles.
//
// Driven by the [active] input:
//   idle   -> calm: slow shape cycling, gentle drift, soft colours (launcher)
//   active -> alive: faster morph + rotation, brighter, glowing (panel while
//             the assistant is thinking / listening)
//
// Fully self-contained. Transparent background (no glow/shade behind it).
// Size in px via [size]. Honours prefers-reduced-motion.
import {
  Component, Input, ElementRef, ViewChild,
  AfterViewInit, OnDestroy, NgZone
} from '@angular/core';

@Component({
  selector: 'app-logix-orb',
  standalone: true,
  template: `<canvas #cv [style.width.px]="size" [style.height.px]="size"
                     role="img" aria-label="Logix AI"></canvas>`,
  styles: [`
    :host { display: inline-flex; line-height: 0; }
    canvas { display: block; }
  `]
})
export class LogixOrbComponent implements AfterViewInit, OnDestroy {
  /** When true the field comes alive (working state); otherwise it drifts calmly. */
  @Input() active = false;
  /** Rendered size in pixels (square). */
  @Input() size = 32;

  @ViewChild('cv', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  private ctx!: CanvasRenderingContext2D;
  private raf = 0;
  private start = 0;
  private dpr = 1;
  private N = 12;

  // Brand-relevant colours the dot gradient flows through (RGB).
  private readonly palette: number[][] = [
    [99, 102, 241],   // indigo-500  #6366F1
    [79, 70, 229],    // indigo-600  #4F46E5
    [124, 58, 237],   // violet-600  #7C3AED
    [139, 92, 246],   // violet-500  #8B5CF6
    [59, 130, 246],   // blue-500    #3B82F6
    [56, 189, 248],   // sky-400     #38BDF8
  ];

  // Per-shape DOT geometry: rnd = corner roundness (1 = circle, 0 = sharp
  // square), arx/ary = dot aspect. Blended between shapes so the dots morph
  // their own outline in step with the macro shape.
  //                    round        square         oval          halftone
  private readonly dotDesc = [
    { rnd: 1,   arx: 1,   ary: 1 },
    { rnd: 0.18, arx: 1,  ary: 1 },
    { rnd: 1,   arx: 1.3, ary: 0.7 },
    { rnd: 1,   arx: 1,   ary: 1 },
  ];
  private readonly LEN = 4;

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    canvas.width = Math.round(this.size * this.dpr);
    canvas.height = Math.round(this.size * this.dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.ctx = ctx;
    this.ctx.scale(this.dpr, this.dpr);

    // Density scales with size so small icons stay crisp, large ones stay rich.
    this.N = Math.max(11, Math.min(20, Math.round(this.size / 2.3)));

    // Always paint ONE frame synchronously so the icon is never blank — even
    // before the first requestAnimationFrame tick, and even in a background
    // tab (where rAF is paused entirely).
    this.start = performance.now();
    this.draw(0);

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;                       // static frame only

    this.startLoop();
    // rAF is frozen while the tab is hidden; resume (and repaint) on return.
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.raf);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private startLoop(): void {
    if (this.raf) return;
    this.ngZone.runOutsideAngular(() => {
      const loop = (now: number) => {
        this.draw((now - this.start) / 1000);
        this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
    });
  }

  private onVisibility = (): void => {
    if (document.hidden) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    } else {
      this.draw((performance.now() - this.start) / 1000);
      this.startLoop();
    }
  };

  constructor(private ngZone: NgZone) {}

  // ---- shape sampler: returns { a: alpha 0..1, s: dot-size 0..~1.25 } ----
  private shape(idx: number, u: number, v: number, t: number): { a: number; s: number } {
    switch (idx) {
      case 0: {                                   // round (halftone disc)
        const r = Math.hypot(u, v);
        return r > 1.03 ? { a: 0, s: 0 } : { a: 1, s: 1 - 0.5 * r };
      }
      case 1: {                                   // square
        const m = Math.max(Math.abs(u), Math.abs(v));
        return m > 1.03 ? { a: 0, s: 0 } : { a: 1, s: 0.9 - 0.28 * m };
      }
      case 2: {                                   // oval
        const rr = Math.hypot(u / 1.18, v / 0.72);
        return rr > 1.03 ? { a: 0, s: 0 } : { a: 1, s: 1 - 0.5 * rr };
      }
      default: {                                  // flowing halftone sheet
        const r = Math.hypot(u, v);
        if (r > 1.05) return { a: 0, s: 0 };
        const n = 0.5 + 0.5 * Math.sin(u * 2.6 + v * 1.7 + t * 1.4);
        return { a: (0.62 + 0.38 * n) * (1 - 0.15 * r), s: (0.42 + 0.8 * n) * (1 - 0.25 * r) };
      }
    }
  }

  private draw(t: number): void {
    const ctx = this.ctx, S = this.size, N = this.N, act = this.active, LEN = this.LEN;
    ctx.clearRect(0, 0, S, S);

    const cx = S / 2, cy = S / 2;
    const breathe = 1 + (act ? 0.06 : 0.035) * Math.sin(t * (act ? 2.4 : 1.4));
    const R = (S / 2) * 0.92 * breathe;
    const spacing = (R * 2) / (N - 1);
    const maxDot = spacing * 0.62;

    const ang = t * (act ? 0.42 : 0.16);
    const cos = Math.cos(ang), sin = Math.sin(ang);

    // shape cycle: round -> square -> oval -> halftone (hold, then morph)
    const SEG = act ? 2.2 : 3.2, HOLD = 0.55;
    const seg = t / SEG;
    const idxA = Math.floor(seg) % LEN;
    const idxB = (idxA + 1) % LEN;
    const frac = seg - Math.floor(seg);
    const blend = frac < HOLD ? 0 : this.sm((frac - HOLD) / (1 - HOLD));

    // blended dot geometry (dots morph their own outline with the shape)
    const dA = this.dotDesc[idxA], dB = this.dotDesc[idxB];
    const rnd = dA.rnd + (dB.rnd - dA.rnd) * blend;
    const arx = dA.arx + (dB.arx - dA.arx) * blend;
    const ary = dA.ary + (dB.ary - dA.ary) * blend;

    const cSpeed = act ? 0.5 : 0.28;
    const pl = this.palette.length;
    const hasRR = typeof (ctx as any).roundRect === 'function';

    ctx.shadowBlur = act ? S * 0.05 : 0;

    for (let i = 0; i < N; i++) {
      const u = (i / (N - 1)) * 2 - 1;
      for (let j = 0; j < N; j++) {
        const v = (j / (N - 1)) * 2 - 1;

        const A = this.shape(idxA, u, v, t);
        const B = this.shape(idxB, u, v, t);
        const a = A.a + (B.a - A.a) * blend;
        if (a <= 0.03) continue;
        let s = A.s + (B.s - A.s) * blend;
        if (s <= 0) continue;
        if (s > 1.25) s = 1.25;

        // rotate the whole field for life
        const rx = u * cos - v * sin;
        const ry = u * sin + v * cos;
        const px = cx + rx * R;
        const py = cy + ry * R;

        // flowing brand-colour gradient across the field
        let cp = (t * cSpeed + (rx + ry) * 0.7 + Math.hypot(u, v) * 0.4) % pl;
        if (cp < 0) cp += pl;
        const i0 = Math.floor(cp), fr = this.sm(cp - i0);
        const c0 = this.palette[i0], c1 = this.palette[(i0 + 1) % pl];
        const rr = Math.round(c0[0] + (c1[0] - c0[0]) * fr);
        const gg = Math.round(c0[1] + (c1[1] - c0[1]) * fr);
        const bb = Math.round(c0[2] + (c1[2] - c0[2]) * fr);

        // gentle per-dot twinkle
        const alpha = a * (0.82 + 0.18 * Math.sin(t * 3 + i * 0.6 + j * 0.35));
        const col = `rgba(${rr},${gg},${bb},${alpha.toFixed(3)})`;
        ctx.fillStyle = col;
        if (act) ctx.shadowColor = col;

        const rad = maxDot * s;
        const hx = rad * arx, hy = rad * ary;

        // draw the dot as a rotated superellipse: circle -> square -> ellipse
        if (rnd > 0.985 && Math.abs(arx - ary) < 0.02) {
          // pure circle fast-path
          ctx.beginPath();
          ctx.arc(px, py, rad, 0, 6.2832);
          ctx.fill();
        } else {
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(ang);
          ctx.beginPath();
          if (hasRR) {
            (ctx as any).roundRect(-hx, -hy, hx * 2, hy * 2, Math.min(hx, hy) * rnd);
          } else {
            ctx.ellipse(0, 0, hx, hy, 0, 0, 6.2832);
          }
          ctx.fill();
          ctx.restore();
        }
      }
    }
    ctx.shadowBlur = 0;
  }

  private sm(x: number): number {
    x = x < 0 ? 0 : x > 1 ? 1 : x;
    return x * x * (3 - 2 * x);
  }
}
